import { useState, useEffect, useCallback, useRef } from 'react'
import { useDispatch } from 'react-redux'
import useRouter from 'utils/useRouter'
import { useVestigioService } from 'hooks/useVestigioService'
import { useSolicitacaoVestigioService } from 'hooks/useSolicitacaoVestigioService'
import { useCestaOperacaoLote } from 'hooks/useCestaOperacaoLote'
import { useAuth } from 'hooks/useAuth'
import { setIdVestigio } from 'actions'
import { capitalizeFirstLetterPhrase } from 'utils/helpers'
import constants from 'utils/constants'

export const useOperacaoVestigioLote = (setOpenMenuOperacao) => {
  const dispatch = useDispatch()
  const router = useRouter()

  const vestigioService = useVestigioService()
  const solicitacaoVestigioService = useSolicitacaoVestigioService()

  const {
    vestigiosComCadeiaCustodia,
    removerVestigio,
    processandoVestigiosComCadeiaCustodia,
    setVestigiosSelecionados,
    setReloadListeners,
    setReloadSelecionados,
  } = useCestaOperacaoLote()

  const { currentUser, verificarPermissao } = useAuth()

  const [form, setForm] = useState({
    lacre: null,
    setOrig: null,
    usuOrig: null,
    usuDest: null,
    localizacao: null,
    tipoEmbalagem: null,
    outroTipoEmbalagem: null,
    setDest: null,
    observacao: null,
    usuarioDestino: null,
    data: null,
    numDocumento: null,
    usuAutorizacao: null,
  })

  const [operacao, setOperacao] = useState(null)
  const [openForm, setOpenForm] = useState(false)
  const [processandoOperacao, setProcessandoOperacao] = useState(false)
  const [formError, setFormError] = useState(false)
  const [manterVestigioNaCesta, setManterVestigioNaCesta] = useState(false)
  const [abrirConfirmarIdentidade, setAbrirConfirmarIdentidade] = useState(false)
  const [abrirAviso, setAbrirAviso] = useState(false)

  const autenticado = useRef(false)
  const cancelado = useRef(false)
  const aceito = useRef(false)

  const resetForm = useCallback(() => {
    setForm({
      lacre: null,
      setOrig: null,
      usuOrig: null,
      usuDest: null,
      localizacao: null,
      tipoEmbalagem: null,
      outroTipoEmbalagem: null,
      observacao: null,
      setDest: null,
      usuarioDestino: null,
      data: null,
      numDocumento: null,
      usuAutorizacao: null,
    })
  }, [])

  const confirmarOperacao = useCallback(async () => {
    setProcessandoOperacao(true)

    if (operacao === 'RETORNAR') {
      await vestigioService.retornarMultiplosVestigios(
        vestigiosComCadeiaCustodia.map((selecionado) => selecionado.id),
        form
      )
    }

    if (operacao === 'GUARDAR') {
      await vestigioService.guardarMultiplosVestigios(
        vestigiosComCadeiaCustodia.map((selecionado) => selecionado.id),
        form
      )
    }

    if (operacao === 'ENCAMINHAR') {
      await vestigioService.encaminharMultiplosVestigios(
        vestigiosComCadeiaCustodia.map((selecionado) => selecionado.id),
        form
      )
    }

    if (operacao === 'ENCAMINHAR_COM_CONFIRMACAO') {
      setAbrirConfirmarIdentidade(true)
      while (!(autenticado.current || cancelado.current)) {
        await new Promise((resolve) => setTimeout(resolve, 1000))
      }
      if (!cancelado.current) {
        autenticado.current = false
        cancelado.current = false
        const idSetDestino = form.setDest.id
        const listaPassagem = vestigiosComCadeiaCustodia.filter((v) => v.setAtual?.id === idSetDestino)
        const listaEncaminhamento = vestigiosComCadeiaCustodia.filter((v) => v.setAtual?.id !== idSetDestino)

        if (listaPassagem.length > 0) {
          await vestigioService.solicitarMultiplasPassagemCustodia(
            listaPassagem.map((v) => v.id),
            form
          )
          await vestigioService.aceitarMultiplasPassagemCustodia(
            listaPassagem.map((v) => v.id),
            form
          )
        }

        if (listaEncaminhamento.length > 0) {
          await vestigioService.encaminharMultiplosVestigios(
            listaEncaminhamento.map((v) => v.id),
            form
          )
          await vestigioService.aceitarMultiplosEncaminhamentos(
            listaEncaminhamento.map((v) => v.id),
            form
          )
        }
      }
    }

    if (operacao === 'ENTREGAR') {
      await vestigioService.entregarMultiplosVestigios(
        vestigiosComCadeiaCustodia.map((v) => v.id),
        {...form, entregaConfirmada: false}
      )
    }

    if (operacao === 'ENTREGAR_COM_CONFIRMACAO') {
      setAbrirConfirmarIdentidade(true)
      while (!(autenticado.current || cancelado.current)) {
        await new Promise((resolve) => setTimeout(resolve, 1000))
      }
      if (!cancelado.current) {
        autenticado.current = false
        cancelado.current = false
        await vestigioService.entregarMultiplosVestigios(
          vestigiosComCadeiaCustodia.map((v) => v.id),
          {...form, entregaConfirmada: true}
        )
      }
    }

    if (operacao === 'DESCARTAR') {
      setAbrirAviso(true)
      while (!(aceito.current || cancelado.current)) {
        await new Promise((resolve) => setTimeout(resolve, 1000))
      }
      if (!cancelado.current) {
        aceito.current = false
        cancelado.current = false
        await vestigioService.descartarMultiplosVestigios(
          vestigiosComCadeiaCustodia.map((selecionado)=> (selecionado.id)),
          form
        )
      }
    }

    if (operacao === 'ALTERAR_SITUACAO') {
      await vestigioService.alterarSituacaoMultiplosVestigios(
        vestigiosComCadeiaCustodia.map((selecionado) => selecionado.id),
        form
      )
    }

    if (operacao === 'ACEITAR_ENCAMINHAMENTO') {
      await vestigioService.aceitarMultiplosEncaminhamentos(
        vestigiosComCadeiaCustodia.map((selecionado) => selecionado.id)
      )
    }

    if (operacao === 'NEGAR_ENCAMINHAMENTO') {
      await vestigioService.negarMultiplosEncaminhamentos(
        vestigiosComCadeiaCustodia.map((selecionado) => selecionado.id)
      )
    }

    if (operacao === 'PASSAR_CUSTODIA') {
      await vestigioService.solicitarMultiplasPassagemCustodia(
        vestigiosComCadeiaCustodia.map((v) => v.id),
        form
      )
    }

    if (operacao === 'ACEITAR_PASSAGEM_CUSTODIA') {
      await vestigioService.aceitarMultiplasPassagemCustodia(
        vestigiosComCadeiaCustodia.map((selecionado) => selecionado.id)
      )
    }

    if (operacao === 'NEGAR_PASSAGEM_CUSTODIA') {
      await vestigioService.negarMultiplasPassagemCustodia(
        vestigiosComCadeiaCustodia.map((selecionado) => selecionado.id)
      )
    }

    if (operacao === 'RETIRAR') {
      await vestigioService.retirarMultiplosVestigios(
        vestigiosComCadeiaCustodia.map((selecionado) => selecionado.id)
      )
    }

    if (operacao === 'CONSUMIR') {
      await vestigioService.consumirMultiplosVestigios(
        vestigiosComCadeiaCustodia.map((selecionado) => selecionado.id),
        form
      )
    }

    if (operacao === 'SOLICITAR') {
      await solicitacaoVestigioService.criarSolicitacaoVestigio(
        vestigiosComCadeiaCustodia.map((selecionado) => selecionado.id),
        form
      )
    }

    if (operacao === 'CRIAR_NOVO_LOTE') {
      await vestigioService.cadastrarEAdicionarEmLote(
        vestigiosComCadeiaCustodia.map((selecionado) => selecionado.id)
      )
    }

    resetForm()
    setProcessandoOperacao(false)
    setOpenForm(false)

    if (manterVestigioNaCesta) {
      setReloadSelecionados(true)
    } else {
      setVestigiosSelecionados([])
      setManterVestigioNaCesta(false)
    }

    setOpenMenuOperacao(false)
    setOperacao(null)
    setReloadListeners(true)
  }, [operacao, vestigiosComCadeiaCustodia, form, manterVestigioNaCesta, resetForm, setOpenMenuOperacao, setReloadListeners, setReloadSelecionados, setVestigiosSelecionados])

  const verificarTodosEntregues = useCallback(() => {
    return vestigiosComCadeiaCustodia.every((selecionado) => selecionado.entregue)
  }, [vestigiosComCadeiaCustodia])

  const verificarTodosComUsuario = useCallback(() => {
    return vestigiosComCadeiaCustodia.every(
      (selecionado) =>
        selecionado.statusCadeiaCustodia === 'COM_USUARIO' &&
        selecionado.id_usuario_atual === currentUser.id
    )
  }, [vestigiosComCadeiaCustodia, currentUser.id])

  const verificarUsuarioPertenceSetorAtualVestigio = useCallback(() => {
    return vestigiosComCadeiaCustodia.every((selecionado) => {
      if (!selecionado.setAtual) return false
      return currentUser.setores.some((setor) => setor.id === selecionado.setAtual.id)
    })
  }, [vestigiosComCadeiaCustodia, currentUser.setores])

  const verificarUsuarioPertenceSetorDestinoVestigio = useCallback(() => {
    return vestigiosComCadeiaCustodia.every((selecionado) => {
      if (selecionado.statusCadeiaCustodia !== 'ENCAMINHADO') return false
      if (!selecionado.setDest) return false
      return currentUser.setores.some((setor) => setor.id === selecionado.setDest.id)
    })
  }, [vestigiosComCadeiaCustodia, currentUser.setores])

  const verificarTodosGuardadosNoSetorDeUsuario = useCallback(() => {
    return vestigiosComCadeiaCustodia.every(
      (selecionado) =>
        selecionado.statusCadeiaCustodia === 'GUARDADO' &&
        verificarUsuarioPertenceSetorAtualVestigio()
    )
  }, [vestigiosComCadeiaCustodia, verificarUsuarioPertenceSetorAtualVestigio])

  const verificarTodosGuardadosNoMesmoSetor = useCallback(() => {
    const setorAtual = vestigiosComCadeiaCustodia?.length > 0 ? vestigiosComCadeiaCustodia[0].setAtual?.id : null
    return vestigiosComCadeiaCustodia.every(
      (selecionado) =>
        selecionado.statusCadeiaCustodia === 'GUARDADO' &&
        selecionado.setAtual?.id === setorAtual
    )
  }, [vestigiosComCadeiaCustodia])

  const ultimaAcao = useCallback((acoes, tipo) => {
    let ultima = null
    acoes.forEach((acao) => {
      if (acao.tipo === tipo) {
        if (!ultima) {
          ultima = acao
        } else if (acao.id > ultima.id) {
          ultima = acao
        }
      }
    })
    return ultima
  }, [])

  const verificarUsuarioDestinoPassagemCustodia = useCallback(() => {
    return vestigiosComCadeiaCustodia.every((v) => {
      const acao = ultimaAcao(v.acoes, 'PASSAGEM_CUSTODIA')
      if (!acao) return false
      if (acao.passagem?.status !== 'SOLICITADO') return false
      return acao.passagem?.id_usuario_destino === currentUser.id
    })
  }, [vestigiosComCadeiaCustodia, currentUser.id, ultimaAcao])

  const verificarTodosEmPassagemCustodia = useCallback(() => {
    return vestigiosComCadeiaCustodia.every(
      (v) => v.statusCadeiaCustodia === 'EM_PASSAGEM_CUSTODIA'
    )
  }, [vestigiosComCadeiaCustodia])

  const changeForm = useCallback((campo, valor) => {
    setForm((prevState) => ({
      ...prevState,
      [campo]: valor,
    }))
  }, [])

  useEffect(() => {
    if (vestigiosComCadeiaCustodia.length === 1) {
      changeForm('lacre', vestigiosComCadeiaCustodia[0].lacre)
      changeForm('tipoEmbalagem', vestigiosComCadeiaCustodia[0].tipoEmbalagem)
      changeForm('outroTipoEmbalagem', vestigiosComCadeiaCustodia[0].outroTipoEmbalagem)
    } else {
      changeForm('lacre', null)
    }
  }, [vestigiosComCadeiaCustodia, changeForm])

  const handleClickAcao = useCallback((acao) => {
    setOpenForm(true)
    setOperacao(acao)
  }, [])

  const custodiaAtual = useCallback((vestigio) => {
    if (vestigio.statusCadeiaCustodia === 'GUARDADO') {
      const guardAction = ultimaAcao(vestigio.acoes, 'GUARDAMENTO')
      return `${guardAction?.guard?.localizacao} - ${vestigio.setAtual.nome}` || ''
    }
    if (vestigio.statusCadeiaCustodia === 'ENCAMINHADO') {
      return `${capitalizeFirstLetterPhrase(vestigio.usuAtual.nome)}`
    }
    if (vestigio.statusCadeiaCustodia === 'EM_PASSAGEM_CUSTODIA') {
      return `${capitalizeFirstLetterPhrase(vestigio.usuAtual.nome)}`
    }
    if (vestigio.statusCadeiaCustodia === 'DESCARTADO') {
      return 'Vestígio descartado'
    }
    if (vestigio.statusCadeiaCustodia === 'COM_USUARIO') {
      return `${capitalizeFirstLetterPhrase(vestigio.usuAtual.nome)}`
    }
    if (vestigio.statusCadeiaCustodia === 'CONSUMIDO') {
      return 'Vestígio consumido'
    }
    return ''
  }, [ultimaAcao])

  const handleCancelarOperacao = useCallback(() => {
    setOperacao(null)
    setOpenForm(false)
  }, [])

  const handleClickVestigio = useCallback((id) => {
    dispatch(setIdVestigio(id))
    router.history.push(`/casos/caso/vestigio/detalhar/${id}`)
    setOpenMenuOperacao(false)
  }, [dispatch, router.history, setOpenMenuOperacao])

  useEffect(() => {
    let error = false

    if (operacao === 'GUARDAR') {
      if (!form.localizacao?.length) {
        error = true
      }
    }

    if (operacao === 'RETORNAR') {
      if (!form.setOrig || !form.usuOrig || !form.usuDest) {
        error = true
      }
    }

    if (operacao === 'ENCAMINHAR') {

      if (!form.setDest) {
        error = true
      }

      if (form.setDest?.id === 184) {

        if (!form.situacaoVest) {
          error = true
        }

        if (form.situacaoVest === constants.STATUS_CENTRAL_CUSTODIA.PARA_ENTREGA && !form.setEntregaVest) {
          error = true
        }
      }
    }

    if (operacao === 'DESCARTAR') {
      if (!form.usuAutorizacao || !form.data || !form.numDocumento)
        error = true
    }

    if (operacao === 'ALTERAR_SITUACAO') {

      if (!form.situacaoVest) {
        error = true
      }
      if (form.situacaoVest === constants.STATUS_CENTRAL_CUSTODIA.PARA_ENTREGA_DESCARTE && (!form.numDocumento || !form.setEntregaVest)) {
        error = true
      }
      if (form.situacaoVest === constants.STATUS_CENTRAL_CUSTODIA.PARA_ENTREGA && !form.setEntregaVest) {
        error = true
      }
    }
    
    if (['ENCAMINHAR_COM_CONFIRMACAO','ENTREGAR_COM_CONFIRMACAO'].includes(operacao)) {

      if (!form.setDest || !form.usuarioDestino) {
        error = true
      }

      if (form.setDest?.id === 184) {

        if (!form.situacaoVest) {
          error = true
        }

        if (form.situacaoVest === constants.STATUS_CENTRAL_CUSTODIA.PARA_ENTREGA && !form.setEntregaVest) {
          error = true
        }
      }
    }
    if (operacao === 'PASSAR_CUSTODIA') {
      if (!form.usuarioDestino) {
        error = true
      }
    }

    if (operacao === 'CONSUMIR') {
      if (!form.exameRealizado) {
        error = true
      }
    }

    setFormError(error)
  }, [form, operacao])

  return {
    form,
    setForm,
    operacao,
    openForm,
    setOpenForm,
    processandoOperacao,
    formError,
    manterVestigioNaCesta,
    setManterVestigioNaCesta,
    abrirConfirmarIdentidade,
    setAbrirConfirmarIdentidade,
    abrirAviso,
    setAbrirAviso,
    aceito,
    autenticado,
    cancelado,
    vestigiosComCadeiaCustodia,
    processandoVestigiosComCadeiaCustodia,
    setVestigiosSelecionados,
    currentUser,
    verificarPermissao,
    confirmarOperacao,
    verificarTodosEntregues,
    verificarTodosComUsuario,
    verificarUsuarioPertenceSetorAtualVestigio,
    verificarUsuarioPertenceSetorDestinoVestigio,
    verificarTodosGuardadosNoSetorDeUsuario,
    verificarTodosGuardadosNoMesmoSetor,
    verificarUsuarioDestinoPassagemCustodia,
    verificarTodosEmPassagemCustodia,
    changeForm,
    handleClickAcao,
    custodiaAtual,
    handleCancelarOperacao,
    handleClickVestigio,
    removerVestigio,
  }
}
